import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import { DataGridPremium, type GridPaginationModel } from '@mui/x-data-grid-premium'
import type { EndOfDayStatement } from '@mynt/backoffice-api-spec/end-of-day-statements/models'
import dayjs from 'dayjs'
import Spacings from '../../figma/tokens/Spacings'
import FigmaBox from '../../mynt-components/components/FigmaBox'
import { YYYY_MM_DD } from '../../tiger/Constants'
import { formatAmount } from '../../tiger/libs/Common'
import BankStatementDetails from './BankStatementDetails'

type BankStatementsTableProps = {
  statements: EndOfDayStatement[]
  isLoading?: boolean
  paginationModel: GridPaginationModel
  onPaginationModelChange: (model: GridPaginationModel) => void
  rowCount: number
}

export const BankStatementsTable = ({
  statements,
  isLoading,
  paginationModel,
  onPaginationModelChange,
  rowCount,
}: BankStatementsTableProps) => {
  const showMinHeight = statements.length === 0 || isLoading

  return (
    <DataGridPremium
      sx={{ height: showMinHeight ? 300 : 'auto' }}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      rowCount={rowCount}
      initialState={{
        sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
      }}
      pageSizeOptions={[10, 25, 50, 100]}
      getRowHeight={() => 'auto'}
      getDetailPanelContent={({ row }) => (
        <FigmaBox fullWidth fullPadding spacing={Spacings.large}>
          <BankStatementDetails statement={row} />
        </FigmaBox>
      )}
      getDetailPanelHeight={() => 'auto'}
      loading={isLoading}
      slots={{
        loadingOverlay: () => <LinearProgress />,
        noRowsOverlay: () => (
          <Stack
            sx={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            No bank statements found
          </Stack>
        ),
      }}
      rows={statements}
      columnHeaderHeight={64}
      columns={[
        {
          field: 'date',
          headerName: 'Date',
          type: 'date',
          minWidth: 120,
          valueFormatter: (value) => dayjs(value).format(YYYY_MM_DD),
          valueGetter: (value) => dayjs(value).toDate(),
        },
        {
          field: 'accountName',
          headerName: 'Account Name',
          minWidth: 180,
          flex: 1,
        },
        {
          field: 'counterPartyName',
          headerName: 'Counter Party',
          minWidth: 200,
          flex: 1,
          valueGetter: (_value, row) => row.counterParty?.name ?? '',
        },
        {
          field: 'amount.value',
          headerName: 'Amount',
          minWidth: 150,
          valueGetter: (_value, row) => row.amount?.value,
          renderCell: ({ row }) => {
            const amount = row.amount
            if (!amount) return ''
            return formatAmount(amount)
          },
        },
        {
          field: 'reference',
          headerName: 'Reference',
          minWidth: 180,
          flex: 1,
        },
        {
          field: 'bankTransactionCode.domain',
          headerName: 'Domain',
          minWidth: 120,
          valueGetter: (_value, row) => row.bankTransactionCode?.domain ?? '',
        },
        {
          field: 'bankTransactionCode.family',
          headerName: 'Family',
          minWidth: 120,
          valueGetter: (_value, row) => row.bankTransactionCode?.family ?? '',
        },
        {
          field: 'endToEndId',
          headerName: 'End-to-End ID',
          minWidth: 200,
        },
      ]}
    />
  )
}
